import { ProviderType } from '../../../enums';

const PROVIDER_STUB: CMxAPI.Provider = {
  active: false,
  degree: '',
  displayName: '',
  identifiers: [],
  id: -1,
  providerUid: '',
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  prefix: '',
  suffix: '',
  providerType: '',
  pager: '',
  isPcpProvider: false,
  encounterId: '',
  providerServiceLocation: '',
  locations: [],
  specialties: [],
  subspecialties: []
};

const IDENTIFIER_STUB: CMxAPI.ProviderIdentifier = {
  id: -1,
  idNumber: '-1',
  idTypeCode: 'EPIC',
  identifierType: 'IDNO',
  checkDigit: null,
  checkDigitScheme: null,
  assigningAuthority: {
    id: -1,
    guid: {
      uuid: ''
    },
    name: null,
    description: null,
    namespaceId: '',
    universalId: null,
    universalIdType: null,
    defaultIssuer: false,
    assigningFacility: null
  },
  entityId: null,
  entityType: null,
  primary: true
};

const convert = (
  xcProvider: XCaliberAPI.ProviderEntity,
  isIPC: boolean,
  isPathology: boolean = false
): CMxAPI.Provider => {
  return {
    ...PROVIDER_STUB,
    active: xcProvider.status === 'ACTIVE',
    displayName: xcProvider.prov_name,
    degree: xcProvider.prov_cred,
    identifiers: [
      {
        ...IDENTIFIER_STUB,
        identifierType: isIPC ? 'PROVID' : isPathology ? 'EPIC' : 'IDNO',
        idNumber: xcProvider.prov_id
      }
    ]
  };
};
const convertIPC = (
  xcProvider: XCaliberAPI.ProviderEntityIPC,
  isBillableSelectable: boolean,
  isSelectable: boolean = true
): CMxCommonApp.ProviderConfig => {
  return {
    ...PROVIDER_STUB,
    displayName: xcProvider.provider_name,
    degree: xcProvider.prov_cred,
    identifiers: [
      {
        ...IDENTIFIER_STUB,
        identifierType: 'PROVID',
        idNumber: xcProvider.provider_id
      }
    ],
    specialties: xcProvider.specialties,
    isBillableSelectable,
    isSelectable
  };
};

export const convertXcaliberEntitiesToCMX = (
  providers: XCaliberAPI.ProviderEntity[],
  isIPC: boolean = false,
  isPathology: boolean = false
) => {
  return providers.map(provider => convert(provider, isIPC, isPathology));
};

export const convertXcaliberEntitiesToIPC = (
  providers: XCaliberAPI.ProviderEntityIPC[],
  isBillableSelectable: boolean,
  shouldFilterActive: boolean = false,
  shouldFilterOutExternalProviders: boolean = false,
  shouldGreyOutExternalProviders: boolean = false,
  providerType: string,
  roleTypeConfig: CMxCommonApp.ProviderRoleTypeConfigRow[]
) => {
  const isPerformigProviderSearch =
    providerType === ProviderType.PERFORMING_PROVIDER;

  const isMatchingRole = (specialty): boolean => {
    return roleTypeConfig.some(
      role =>
        role?.['Provider Role Type']?.toLowerCase() ===
          specialty?.name?.toLowerCase() &&
        role?.['Performing Eligible'] === 'T'
    );
  };

  const filters = [];
  if (shouldFilterActive) {
    filters.push(provider => provider?.active === 'true');
  }

  if (shouldFilterOutExternalProviders) {
    filters.push(provider => !isExternalProvider(provider));
  }

  let filteredProviders = [...providers];
  filters.forEach(providerFilter => {
    filteredProviders = filteredProviders.filter(providerFilter);
  });

  return filteredProviders.map(provider => {
    const specialties = provider?.specialties ?? [];
    const hasIncludedRole = isPerformigProviderSearch
      ? specialties.some(isMatchingRole)
      : true;

    let isSelectable =
      (shouldGreyOutExternalProviders ? !isExternalProvider(provider) : true) &&
      hasIncludedRole;

    return convertIPC(provider, isBillableSelectable, isSelectable);
  });
};

export const isExternalProvider = (provider: XCaliberAPI.ProviderEntityIPC) => {
  return (
    provider?.specialties?.every(
      specialty =>
        specialty?.report_grouper_six
          ?.toLowerCase()
          ?.includes('external provider') ||
        specialty?.report_grouper_six
          ?.toLowerCase()
          ?.includes('external physician') ||
        specialty?.employment_designation
          ?.toLowerCase()
          ?.includes('external provider') ||
        specialty?.employment_designation
          ?.toLowerCase()
          ?.includes('external physician')
    ) ?? false
  );
};
